/** KAN-718: письмо целиком: заголовки, тело как текст, вложения, переход к переписке клиента. */
import { MessagesSquare, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEmail } from '@/hooks/useEmails';
import { EMAIL_DIRECTION_LABEL } from '@/lib/emailFormat';
import { formatCheckedAt } from '@/lib/telegramProxyDirectory';
import type { EmailItem } from '@/types/emailMailbox';

interface Props {
  /** Строка списка: заголовки показываются сразу, тело догружается. */
  email: EmailItem | null;
  onClose: () => void;
  onOpenClient: (email: EmailItem) => void;
}

function Header({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-16 shrink-0 text-gray-500">{label}</span>
      <span className="break-all">{value || '-'}</span>
    </div>
  );
}

export function EmailDetailDialog({ email, onClose, onOpenClient }: Props): JSX.Element {
  const { data: detail, isLoading, isError } = useEmail(email?.id ?? null);
  const shown = detail ?? email;

  return (
    <Dialog open={!!email} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="email-detail">
        {shown && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-6">{shown.subject || '(без темы)'}</DialogTitle>
              <DialogDescription>{EMAIL_DIRECTION_LABEL[shown.direction]} письмо</DialogDescription>
            </DialogHeader>
            <div className="space-y-1" data-testid="email-detail-headers">
              <Header label="От" value={shown.from} />
              <Header label="Кому" value={shown.to} />
              <Header label="Дата" value={formatCheckedAt(shown.created_at)} />
              {shown.client_id && <Header label="Клиент" value={shown.client_name} />}
            </div>
            {isLoading && <p className="text-sm text-gray-500">Загрузка письма...</p>}
            {isError && <p className="text-sm text-red-700">Не удалось загрузить письмо</p>}
            {detail && (
              <pre
                className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-md border bg-gray-50 p-3 font-sans text-sm"
                data-testid="email-detail-body"
              >
                {detail.body || '(пустое письмо)'}
              </pre>
            )}
            {shown.attachments.length > 0 && (
              <div data-testid="email-detail-attachments">
                <p className="mb-1 text-xs font-medium text-gray-500">Вложения</p>
                <ul className="space-y-1">
                  {shown.attachments.map((a, i) => (
                    <li key={`${a.title}-${i}`} className="flex items-center gap-2 text-sm">
                      <Paperclip className="h-3.5 w-3.5 text-gray-500" />
                      {a.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {shown.client_id && (
              <DialogFooter>
                <Button data-testid="email-detail-open-client" onClick={() => onOpenClient(shown)}>
                  <MessagesSquare className="mr-1 h-4 w-4" />
                  Открыть переписку клиента
                </Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
